// MediaService - Core image handling, validation, and optimization
class MediaService {
  // Platform-specific image requirements
  static PLATFORM_RULES = {
    linkedin: {
      maxSize: 5 * 1024 * 1024,      // 5MB
      maxImages: 20,
      formats: ['jpeg', 'jpg', 'png'],
      minWidth: 400,
      maxWidth: 7680,
      minHeight: 400,
      maxHeight: 4320,
      aspectRatios: {
        min: 0.5,  // 1:2
        max: 2.0   // 2:1
      }
    },
    twitter: {
      maxSize: 5 * 1024 * 1024,      // 5MB
      maxImages: 4,
      formats: ['jpeg', 'jpg', 'png', 'gif', 'webp'],
      minWidth: 100,
      maxWidth: 8192,
      minHeight: 100,
      maxHeight: 8192,
      aspectRatios: {
        min: 0.25, // 1:4  
        max: 4.0   // 4:1
      }
    }
  };

  // Supported MIME types mapping
  static MIME_TYPES = {
    'image/jpeg': 'jpeg',
    'image/jpg': 'jpg', 
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp'
  };

  /**
   * Validate image file against platform requirements
   * @param {File} file - Image file to validate
   * @param {string} platform - Target platform ('linkedin' or 'twitter')
   * @returns {Promise<{valid: boolean, errors: string[], warnings: string[]}>}
   */
  static async validateImage(file, platform = 'linkedin') {
    const errors = [];
    const warnings = [];
    const rules = this.PLATFORM_RULES[platform];

    if (!rules) {
      errors.push(`Unsupported platform: ${platform}`);
      return { valid: false, errors, warnings };
    }

    // Check if file exists
    if (!file) {
      errors.push('No file provided');
      return { valid: false, errors, warnings };
    }

    // Check file size
    if (file.size > rules.maxSize) {
      errors.push(`File size ${this.formatFileSize(file.size)} exceeds maximum ${this.formatFileSize(rules.maxSize)}`);
    }

    // Check MIME type
    const format = this.MIME_TYPES[file.type];
    if (!format) {
      errors.push(`Unsupported file type: ${file.type}`);
      return { valid: false, errors, warnings };
    }

    if (!rules.formats.includes(format)) {
      errors.push(`${format.toUpperCase()} format not supported for ${platform}. Supported: ${rules.formats.join(', ')}`);
    }

    // Check image dimensions
    try {
      const dimensions = await this.getImageDimensions(file);
      
      if (dimensions.width < rules.minWidth || dimensions.height < rules.minHeight) {
        errors.push(`Image too small: ${dimensions.width}x${dimensions.height}. Minimum: ${rules.minWidth}x${rules.minHeight}`);
      }
      
      if (dimensions.width > rules.maxWidth || dimensions.height > rules.maxHeight) {
        warnings.push(`Image very large: ${dimensions.width}x${dimensions.height}. Consider resizing for faster upload.`);
      }

      // Check aspect ratio
      const aspectRatio = dimensions.width / dimensions.height;
      if (aspectRatio < rules.aspectRatios.min || aspectRatio > rules.aspectRatios.max) {
        warnings.push(`Unusual aspect ratio: ${aspectRatio.toFixed(2)}. ${platform} works best with ratios between ${rules.aspectRatios.min}-${rules.aspectRatios.max}`);
      }

    } catch (dimensionError) {
      errors.push('Could not read image dimensions. File may be corrupted.');
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Get image dimensions from file
   * @param {File} file - Image file
   * @returns {Promise<{width: number, height: number}>}
   */
  static getImageDimensions(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };
      
      img.src = url;
    });
  }

  /**
   * Compress image if needed to fit platform requirements
   * @param {File} file - Original image file
   * @param {string} platform - Target platform
   * @param {number} quality - Compression quality (0.1-1.0)
   * @returns {Promise<File>}
   */
  static async compressImage(file, platform = 'linkedin', quality = 0.85) {
    const rules = this.PLATFORM_RULES[platform];
    
    // If file is already under size limit and not too large, return as-is
    if (file.size <= rules.maxSize * 0.8) {
      const dimensions = await this.getImageDimensions(file);
      if (dimensions.width <= rules.maxWidth && dimensions.height <= rules.maxHeight) {
        return file;
      }
    }

    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        try {
          const { width, height } = this.calculateOptimalDimensions(
            img.naturalWidth, 
            img.naturalHeight, 
            rules.maxWidth, 
            rules.maxHeight
          );

          canvas.width = width;
          canvas.height = height;
          
          // Draw and compress
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            URL.revokeObjectURL(url);
            
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }

            // Create new File object
            const compressedFile = new File(
              [blob], 
              file.name, 
              { 
                type: file.type.startsWith('image/png') ? 'image/png' : 'image/jpeg',
                lastModified: Date.now() 
              }
            );

            resolve(compressedFile);
          }, file.type.startsWith('image/png') ? 'image/png' : 'image/jpeg', quality);
          
        } catch (error) {
          URL.revokeObjectURL(url);
          reject(error);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image for compression'));
      };

      img.src = url;
    });
  }

  /**
   * Calculate optimal dimensions while preserving aspect ratio
   * @param {number} originalWidth
   * @param {number} originalHeight  
   * @param {number} maxWidth
   * @param {number} maxHeight
   * @returns {{width: number, height: number}}
   */
  static calculateOptimalDimensions(originalWidth, originalHeight, maxWidth, maxHeight) {
    const aspectRatio = originalWidth / originalHeight;
    
    let width = originalWidth;
    let height = originalHeight;
    
    // Scale down if too large
    if (width > maxWidth) {
      width = maxWidth;
      height = width / aspectRatio;
    }
    
    if (height > maxHeight) {
      height = maxHeight;
      width = height * aspectRatio;
    }
    
    return {
      width: Math.round(width),
      height: Math.round(height)
    };
  }

  /**
   * Convert image to supported format if needed
   * @param {File} file - Original file
   * @param {string} platform - Target platform  
   * @returns {Promise<File>}
   */
  static async convertToSupportedFormat(file, platform = 'linkedin') {
    const rules = this.PLATFORM_RULES[platform];
    const currentFormat = this.MIME_TYPES[file.type];
    
    // If already supported, return as-is
    if (rules.formats.includes(currentFormat)) {
      return file;
    }

    // Convert to JPEG (most universally supported)
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        
        // Fill with white background for PNG transparency
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          
          if (!blob) {
            reject(new Error('Failed to convert image format'));
            return;
          }

          const convertedFile = new File(
            [blob],
            file.name.replace(/\.[^/.]+$/, '.jpg'),
            { 
              type: 'image/jpeg',
              lastModified: Date.now()
            }
          );

          resolve(convertedFile);
        }, 'image/jpeg', 0.9);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image for conversion'));
      };

      img.src = url;
    });
  }

  /**
   * Process image for specific platform (validate, convert, compress)
   * @param {File} file - Original image file
   * @param {string} platform - Target platform
   * @returns {Promise<{success: boolean, file?: File, errors: string[], warnings: string[]}>}
   */
  static async optimizeForPlatform(file, platform = 'linkedin') {
    try {
      // Step 1: Initial validation
      let validation = await this.validateImage(file, platform);
      let processedFile = file;

      // Step 2: Convert format if needed
      if (!validation.valid && validation.errors.some(e => e.includes('format not supported'))) {
        processedFile = await this.convertToSupportedFormat(file, platform);
        validation = await this.validateImage(processedFile, platform);
      }

      // Step 3: Compress if needed
      if (validation.valid || validation.errors.every(e => e.includes('size') || e.includes('large'))) {
        processedFile = await this.compressImage(processedFile, platform);
        validation = await this.validateImage(processedFile, platform);
      }

      return {
        success: validation.valid,
        file: processedFile,
        errors: validation.errors,
        warnings: validation.warnings
      };

    } catch (error) {
      console.error(`[MediaService] Error processing image for ${platform}:`, error);
      return {
        success: false,
        errors: [`Failed to process image: ${error.message}`],
        warnings: []
      };
    }
  }

  /**
   * Format file size for human reading
   * @param {number} bytes
   * @returns {string}
   */
  static formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Generate image preview URL
   * @param {File} file - Image file
   * @returns {string}
   */
  static generatePreviewUrl(file) {
    return URL.createObjectURL(file);
  }

  /**
   * Cleanup preview URL
   * @param {string} url - Preview URL to cleanup
   */
  static cleanupPreviewUrl(url) {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  }
}

export default MediaService;